import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyseFiles, createFromAnalysis } from '../../service/SessionService';
import { AnalyseResponse, SessionCreationConfig } from '../../models/AnalyseResponse';

// ─── Style constants (matching CreateSessionWizard) ───
const btn = {
  primary: 'gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition text-sm font-medium',
  secondary: 'border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition text-sm',
};
const card = 'bg-white rounded-2xl shadow-sm p-6';

const Toggle = ({ on, onToggle }: { on: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-blue-600' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

// Aggregate unique WIP numbers from the EME/VOR items across all analysed files.
// Since the analyse endpoint returns per-file orderTypeCodes, we can derive the list.
const collectWipNumbers = (result: AnalyseResponse | null): string[] => {
  if (!result) return [];
  const set = new Set<string>();
  for (const f of result.files) {
    if (f.status !== 'INCLUDED') continue;
    for (const c of f.orderTypeCodes ?? []) {
      if (c && c.trim()) set.add(c.trim());
    }
  }
  return Array.from(set);
};

const CreateBmwSessionWizard = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Step 1 state
  const [allFiles, setAllFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [splitOt, setSplitOt] = useState(true);
  const [deliveryNotesInput, setDeliveryNotesInput] = useState('');

  // Step 1 validation
  const [deliveryNotesError, setDeliveryNotesError] = useState('');

  // Step 2 state
  const [analysing, setAnalysing] = useState(false);
  const [analyseResult, setAnalyseResult] = useState<AnalyseResponse | null>(null);
  const [analyseError, setAnalyseError] = useState('');

  // Step 3 state — editable session titles
  const [stockTitle, setStockTitle] = useState('Stock');
  const [emevTitle, setEmevTitle] = useState('EME/VOR');
  const [singleTitle, setSingleTitle] = useState('BMW Session');

  // Step 4 state
  const [createdSessions, setCreatedSessions] = useState<any[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // ─── Derived ───
  const wipNumbers = useMemo(() => collectWipNumbers(analyseResult), [analyseResult]);

  const parsedDeliveryNotes = (): string[] =>
    deliveryNotesInput
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);

  // ─── File handling ───
  const addFiles = (incoming: File[]) => {
    const csvs = incoming.filter(f => f.name.toLowerCase().endsWith('.csv'));
    const existing = new Set(allFiles.map(f => f.name));
    const toAdd = csvs.filter(f => !existing.has(f.name));
    if (toAdd.length) setAllFiles(prev => [...prev, ...toAdd]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  // ─── Analyse ───
  const handleAnalyse = async () => {
    if (parsedDeliveryNotes().length === 0) {
      setDeliveryNotesError('Delivery Notes are required.');
      return;
    }
    setDeliveryNotesError('');
    setAnalyseError('');
    setAnalysing(true);
    setStep(2);

    const formData = new FormData();
    allFiles.forEach(f => formData.append('files', f));
    // BMW has no processing-date metadata in the CSV — never apply date filter.
    formData.append('filterDate', 'false');
    // Always classify server-side so WIP numbers surface in results; the
    // client toggle decides whether to create one or two sessions.
    formData.append('filterOrderType', 'true');

    try {
      const result = await analyseFiles(formData);
      setAnalyseResult(result);
    } catch (err: any) {
      setAnalyseError(err?.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setAnalysing(false);
    }
  };

  // ─── Create sessions ───
  const handleCreate = async () => {
    if (!analyseResult) return;
    setCreating(true);
    setCreateError('');

    const includedFileNames = analyseResult.files
      .filter(f => f.status === 'INCLUDED')
      .map(f => f.fileName);
    const manualDeliveryNotes = parsedDeliveryNotes();
    const sessions: SessionCreationConfig[] = [];

    if (splitOt) {
      if (analyseResult.stockCount > 0) {
        sessions.push({
          title: stockTitle || 'Stock',
          group: 'STOCK',
          fileNames: includedFileNames,
          manualDeliveryNotes: manualDeliveryNotes.length ? manualDeliveryNotes : undefined,
        });
      }
      if (analyseResult.emevCount > 0) {
        sessions.push({
          title: emevTitle || 'EME/VOR',
          group: 'EME_VOR',
          fileNames: includedFileNames,
          manualDeliveryNotes: manualDeliveryNotes.length ? manualDeliveryNotes : undefined,
        });
      }
    } else if (includedFileNames.length) {
      sessions.push({
        title: singleTitle || 'BMW Session',
        group: 'ALL',
        fileNames: includedFileNames,
        manualDeliveryNotes: manualDeliveryNotes.length ? manualDeliveryNotes : undefined,
      });
    }

    if (!sessions.length) {
      setCreateError('No items available to create sessions.');
      setCreating(false);
      return;
    }

    try {
      const created = await createFromAnalysis({ analysisId: analyseResult.analysisId, sessions });
      setCreatedSessions(created);
      setStep(4);
    } catch (err: any) {
      setCreateError(err?.response?.data?.detail || 'Failed to create sessions. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const includedFiles = analyseResult?.files.filter(f => f.status === 'INCLUDED') ?? [];

  // ─── Step 1: Upload + Filters ───
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className={card}>
        <div className="flex items-center mb-4">
          <button onClick={() => navigate('/dashboard/scanning-session')} className="mr-4 text-gray-600 hover:text-gray-900">
            <i className="fas fa-arrow-left" />
          </button>
          <h2 className="text-xl font-semibold text-gray-800">Create BMW Session</h2>
        </div>

        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
          onDragOver={e => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
        >
          <input ref={fileInputRef} type="file" accept=".csv" multiple className="hidden" onChange={handleFileInput} />
          <i className="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-3" />
          <p className="text-gray-500 mb-3 text-sm">Drag & drop CSV files here or click to browse</p>
          <button type="button" className={btn.primary} onClick={() => fileInputRef.current?.click()}>
            Select Files
          </button>
          {allFiles.length > 0 && (
            <p className="mt-3 text-sm font-medium text-blue-600">{allFiles.length} file(s) selected</p>
          )}
        </div>

        {allFiles.length > 0 && (
          <div className="mt-4 border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-48 overflow-y-auto">
            {allFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2">
                <i className="fas fa-file-csv text-green-500 text-sm" />
                <span className="flex-1 text-sm text-gray-700 truncate">{f.name}</span>
                <span className="text-xs text-gray-400">{(f.size / 1024).toFixed(1)} KB</span>
                <button
                  onClick={() => setAllFiles(prev => prev.filter((_, idx) => idx !== i))}
                  className="text-red-500 hover:text-red-700 text-xs bg-red-50 px-2 py-1 rounded"
                >Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {allFiles.length > 0 && (
        <div className={card}>
          <h3 className="text-base font-semibold text-gray-800 mb-4">Options</h3>
          <div className="space-y-4">

            {/* Order Type split */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Split by Order Type</p>
                <p className="text-xs text-gray-500 mt-0.5">Rows with a WIP No → EME/VOR session; rows without → Stock session</p>
              </div>
              <Toggle on={splitOt} onToggle={() => setSplitOt(v => !v)} />
            </div>

            <hr className="border-gray-100" />

            {/* Delivery Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery Notes <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">Separate multiple notes with commas — applied to every session created.</p>
              <input
                type="text"
                value={deliveryNotesInput}
                onChange={e => { setDeliveryNotesInput(e.target.value); if (deliveryNotesError) setDeliveryNotesError(''); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm ${
                  deliveryNotesError
                    ? 'border-red-400 focus:ring-red-300'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="e.g. Note 1, Note 2"
              />
              {deliveryNotesError && (
                <p className="mt-1 text-xs text-red-600">
                  <i className="fas fa-exclamation-circle mr-1" />{deliveryNotesError}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {allFiles.length > 0 && (
        <div className="flex justify-end">
          <button className={`${btn.primary} px-6`} onClick={handleAnalyse}>
            <i className="fas fa-filter mr-2" />Analyse Files
          </button>
        </div>
      )}
    </div>
  );

  // ─── Step 2: Analysing / Results ───
  const renderStep2 = () => {
    if (analysing) return (
      <div className={`${card} text-center py-16`}>
        <div className="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin mx-auto mb-4" />
        <p className="text-gray-600 font-medium">Analysing files...</p>
        <p className="text-gray-400 text-sm mt-1">Parsing CSV rows and extracting WIP numbers</p>
      </div>
    );

    if (analyseError) return (
      <div className={card}>
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation text-red-500" />
          </div>
          <h3 className="font-semibold text-gray-800 mb-2">Analysis Failed</h3>
          <p className="text-gray-500 text-sm mb-4">{analyseError}</p>
          <button className={btn.secondary} onClick={() => { setStep(1); setAnalyseError(''); }}>Go Back</button>
        </div>
      </div>
    );

    if (!analyseResult) return null;

    return (
      <div className="space-y-6">
        <div className={card}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Analysis Results</h2>
            <button className={btn.secondary} onClick={() => setStep(1)}>
              <i className="fas fa-arrow-left mr-2" />Back
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Files analysed', value: analyseResult.totalUploaded, color: 'text-gray-800' },
              { label: splitOt ? 'Stock files' : 'Ready', value: splitOt ? analyseResult.stockCount : includedFiles.length, color: 'text-green-600' },
              ...(splitOt ? [{ label: 'EME/VOR files', value: analyseResult.emevCount, color: 'text-blue-600' }] : []),
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-gray-50 rounded-xl p-4 text-center">
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          {analyseResult.conversionFailed > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-xl text-sm text-red-800 mb-4">
              <i className="fas fa-times-circle mr-2" />
              {analyseResult.conversionFailed} file(s) could not be parsed
            </div>
          )}

          {splitOt && wipNumbers.length > 0 && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r-xl text-sm text-blue-800 mb-4">
              <i className="fas fa-tag mr-2" />
              WIP numbers found: {wipNumbers.join(', ')}
            </div>
          )}

          <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-72 overflow-y-auto">
            {analyseResult.files.map((f, i) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-3 ${f.status !== 'INCLUDED' ? 'opacity-40' : ''}`}>
                <i className={`fas fa-file-csv text-sm ${f.status === 'INCLUDED' ? 'text-green-500' : 'text-gray-400'}`} />
                <span className="flex-1 text-xs text-gray-700 font-mono truncate">{f.fileName}</span>
                {f.status === 'INCLUDED' ? (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Ready</span>
                ) : (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">Failed</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {includedFiles.length > 0 && (
          <div className="flex justify-end">
            <button className={`${btn.primary} px-6`} onClick={() => setStep(3)}>
              Preview Sessions <i className="fas fa-arrow-right ml-2" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // ─── Step 3: Session Preview ───
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className={card}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Session Preview</h2>
          <button className={btn.secondary} onClick={() => setStep(2)}>
            <i className="fas fa-arrow-left mr-2" />Back
          </button>
        </div>

        {splitOt ? (
          <div className="space-y-4">
            {analyseResult && analyseResult.stockCount > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-green-800 text-sm">
                    <i className="fas fa-boxes mr-2" />Stock session (rows with empty WIP No)
                  </p>
                  <input
                    type="text"
                    value={stockTitle}
                    onChange={e => setStockTitle(e.target.value)}
                    className="text-sm border border-green-300 rounded px-2 py-1 bg-white text-gray-700 w-48 text-right"
                    placeholder="Session title"
                  />
                </div>
              </div>
            )}
            {analyseResult && analyseResult.emevCount > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3 gap-3">
                  <p className="font-semibold text-blue-800 text-sm">
                    <i className="fas fa-truck mr-2" />EME/VOR session (rows with WIP No)
                  </p>
                  <input
                    type="text"
                    value={emevTitle}
                    onChange={e => setEmevTitle(e.target.value)}
                    className="text-sm border border-blue-300 rounded px-2 py-1 bg-white text-gray-700 flex-1 max-w-md text-right"
                    placeholder="Session title"
                  />
                </div>
                {wipNumbers.length > 0 && (
                  <p className="text-xs text-blue-700">WIP numbers: {wipNumbers.join(', ')}</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-amber-800 text-sm">
                <i className="fas fa-layer-group mr-2" />Single session — all rows
              </p>
              <input
                type="text"
                value={singleTitle}
                onChange={e => setSingleTitle(e.target.value)}
                className="text-sm border border-amber-300 rounded px-2 py-1 bg-white text-gray-700 w-48 text-right"
                placeholder="Session title"
              />
            </div>
          </div>
        )}

        {parsedDeliveryNotes().length > 0 && (
          <div className="mt-4 text-xs text-gray-500">
            Delivery notes: {parsedDeliveryNotes().join(', ')}
          </div>
        )}

        {createError && (
          <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-3 rounded-r-xl text-sm text-red-800">
            <i className="fas fa-exclamation-circle mr-2" />{createError}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button className={`${btn.primary} px-6`} onClick={handleCreate} disabled={creating}>
          {creating ? (
            <><i className="fas fa-spinner fa-spin mr-2" />Creating...</>
          ) : (
            <><i className="fas fa-check mr-2" />Create Sessions</>
          )}
        </button>
      </div>
    </div>
  );

  // ─── Step 4: Success ───
  const renderStep4 = () => (
    <div className={`${card} text-center`}>
      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i className="fas fa-check text-green-600 text-xl" />
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-2">
        {createdSessions.length} Session{createdSessions.length !== 1 ? 's' : ''} Created
      </h2>
      <div className="mt-4 space-y-3 text-left max-w-md mx-auto">
        {createdSessions.map((s, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-4">
            <p className="font-medium text-gray-800 text-sm">{s.title}</p>
            <p className="text-xs text-gray-500 mt-1">
              {s.deliveryNotes?.length ?? 0} delivery note(s) · {new Date(s.createdDate).toLocaleDateString()}
            </p>
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-3 mt-6">
        <button className={btn.primary} onClick={() => navigate('/dashboard/scanning-session')}>
          Go to Sessions
        </button>
        <button className={btn.secondary} onClick={() => {
          setStep(1); setAllFiles([]); setAnalyseResult(null);
          setCreatedSessions([]); setCreateError('');
        }}>
          Create More
        </button>
      </div>
    </div>
  );

  return (
    <div className="dashboard-section max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-6 text-xs text-gray-500">
        {(['Upload', 'Results', 'Preview', 'Done'] as const).map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
              ${step === i + 1 ? 'gradient-bg text-white' : step > i + 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
              {step > i + 1 ? <i className="fas fa-check text-xs" /> : i + 1}
            </div>
            <span className={step === i + 1 ? 'text-gray-800 font-medium' : ''}>{label}</span>
            {i < 3 && <i className="fas fa-chevron-right text-gray-300" />}
          </div>
        ))}
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </div>
  );
};

export default CreateBmwSessionWizard;
